import * as React from "react";
import { useLocation, useSearchParams } from "react-router";

import { useAuth } from "@/providers";
import type { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord } from "@/shared/domain/views";
import { VIEW_SEARCH_PARAM } from "@/shared/domain/views/viewSearchParam";

import { fingerprint, hasMovedSince } from "../tableFingerprint";
import type { ResourceView, UseResourceViewOptions } from "../useResourceView";
import type { ViewFromLink } from "../ViewPicker/useViewFromLink";

import { isArrivalClaimed } from "./arrivalClaim";
import {
  readSnapshot,
  seedOptionsFromSnapshot,
  snapshotOf,
  writeSnapshot,
} from "./tableMemory";

export interface UseRestoredTableStateOptions<TData> {
  /** Resource type this list shows; memory is kept per table. */
  target: QueryTarget;
  /**
   * What `useViewFromLink` decided. A link outranks memory, so when it seeded
   * the table this hook hands its result through untouched.
   */
  link: ViewFromLink<TData>;
}

export interface RestoredTableState<TData> {
  /** The options to hand `useResourceView`, seeded from whichever source won. */
  options: UseResourceViewOptions<TData>;
  /**
   * The View the table was built from, for `useLoadedView` to mark loaded on
   * the first render, or `null` if it was built from ad-hoc state.
   */
  view: ViewRecord | null;
  /**
   * Whether a link or this tab's memory claimed the arrival, so the default
   * View knows to stand down.
   */
  arrivalClaimed: boolean;
}

/**
 * Start a resource table where this tab left it.
 *
 * Position 2 of the arrival order: behind a `?view=` or `?query=` link, ahead
 * of the customer's default View. Decided once, at mount, in the same pass as the
 * link — `sessionStorage` is readable synchronously, and `useResourceView`
 * reads its options exactly once, so seeding here restores every dimension
 * (filters, sort, page size, columns, and which View is loaded) on the first
 * render. No effect, no extra render, no flash of an unfiltered table, and
 * nothing for `useApplyView`'s sequence counter to race.
 *
 * The memory is left in place when a link wins. `useRecordTableState`
 * overwrites it as soon as the linked View lands, which is the state the
 * customer will expect to return to.
 *
 * Read once, in a state initializer: the org, the route and the link are
 * mount-time facts for this decision. A later org switch is handled by
 * `useLoadedView`, and a later `?view=` is the customer picking from the panel.
 */
export function useRestoredTableState<TData>({
  target,
  link,
}: UseRestoredTableStateOptions<TData>): RestoredTableState<TData> {
  const { currentOrganization } = useAuth();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const [restored] = React.useState<RestoredTableState<TData>>(() => {
    const orgId = currentOrganization?.org_id;
    const linkedId = searchParams.get(VIEW_SEARCH_PARAM) ?? undefined;
    const { fromQueryLink } = link;
    const remembered =
      orgId === undefined || linkedId !== undefined || fromQueryLink
        ? null
        : readSnapshot(orgId, target, pathname);
    const arrivalClaimed = isArrivalClaimed({
      linkedId,
      fromQueryLink,
      snapshot: remembered,
    });

    if (remembered !== null) {
      return {
        options: seedOptionsFromSnapshot(remembered, link.options),
        view: remembered.loadedView,
        arrivalClaimed,
      };
    }
    return { options: link.options, view: link.view, arrivalClaimed };
  });

  return restored;
}

export interface UseRecordTableStateOptions<TData> {
  /** Resource type this list shows; memory is kept per table. */
  target: QueryTarget;
  /** The live resource view to mirror. */
  view: ResourceView<TData>;
  /** The View currently loaded, from `useLoadedView`, so it is restored loaded. */
  loadedView: ViewRecord | null;
}

/**
 * Keep this tab's memory of a resource table current.
 *
 * Writes only once the table has moved from how it mounted, and from then on
 * writes every change. The gate exists for the arrivals that land after
 * mount: a default View, or a RoboQL link, applies a beat later, and a writer
 * that mirrored the first render would store the empty table it saw. If the
 * customer then left before the apply landed, that empty snapshot would
 * outrank their default on every later visit — an accidental reset that
 * sticks. With the gate, leaving early stores nothing, and the default applies
 * next time as it should.
 *
 * A latch rather than a live comparison: sorting away and back would
 * otherwise equal the mount state and go unwritten, leaving the snapshot
 * describing the sort in between.
 *
 * Restoring is a mount-time seed, so there is no apply for this to race and
 * no interlock to hold; the first render already shows the restored state,
 * and until something moves there is nothing new to store.
 */
export function useRecordTableState<TData>({
  target,
  view,
  loadedView,
}: UseRecordTableStateOptions<TData>): void {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;
  const { pathname } = useLocation();

  const mountedRef = React.useRef({ table: fingerprint(view), loadedView });
  const movedRef = React.useRef(false);

  const { filters, table } = view;
  const { sort, pageSize, visibleColumnIds } = table;

  React.useEffect(() => {
    if (!movedRef.current) {
      const moved =
        hasMovedSince(mountedRef.current.table, fingerprint(view)) ||
        loadedView !== mountedRef.current.loadedView;
      if (!moved) {
        return;
      }
      movedRef.current = true;
    }
    if (orgId === undefined) {
      return;
    }
    writeSnapshot(orgId, target, pathname, snapshotOf(view, loadedView));
    // `view` is a fresh object every render (see `useResourceView`); the
    // pieces that can move are listed instead.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orgId,
    target,
    pathname,
    filters,
    sort,
    pageSize,
    visibleColumnIds,
    loadedView,
  ]);
}
